import { Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { portalPath } from "../lib/roles";
import { Loader2 } from "lucide-react";
import { authedFetch } from "../lib/api";

const Loader = () => (
  <div className="flex h-screen w-full items-center justify-center bg-background" data-testid="auth-loading">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

// `allow` = array of roles permitted on this route (e.g. ["gp"]). If the
// signed-in user's role isn't allowed, they're sent to their own portal.
export const ProtectedRoute = ({ children, redirectTo = "/login", allow }) => {
  const { user, loading, role, roleLoading } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (!user || location.pathname.startsWith("/admin/preview/") || location.pathname.startsWith("/admin/view-as/")) return;
    authedFetch("/activity", { method: "POST", body: JSON.stringify({ event_type: "page_view", event_name: location.pathname, path: location.pathname }) }).catch(() => {});
  }, [user, location.pathname]);

  useEffect(() => {
    if (!user || location.pathname.startsWith("/admin/preview/") || location.pathname.startsWith("/admin/view-as/")) return undefined;
    const recordClick = (event) => {
      const target = event.target?.closest?.("button, a, [data-activity]");
      if (!target) return;
      const name = target.getAttribute("data-activity") || target.getAttribute("data-testid") || target.getAttribute("aria-label") || target.textContent?.trim().replace(/\s+/g, " ").slice(0, 80);
      if (!name) return;
      authedFetch("/activity", { method: "POST", body: JSON.stringify({ event_type: "click", event_name: name, path: location.pathname }) }).catch(() => {});
    };
    document.addEventListener("click", recordClick, true);
    return () => document.removeEventListener("click", recordClick, true);
  }, [user, location.pathname]);

  if (loading) return <Loader />;
  if (!user) return <Navigate to={redirectTo} replace state={{ from: location.pathname + location.search + location.hash }} />;

  if (allow) {
    if (roleLoading) return <Loader />;
    if (!allow.includes(role)) return <Navigate to={portalPath(role)} replace />;
  }

  return children;
};

export const PublicRoute = ({ children }) => {
  const { user, loading, role, roleLoading } = useAuth();

  if (loading) return <Loader />;
  if (user) {
    if (roleLoading) return <Loader />;
    return <Navigate to={portalPath(role)} replace />;
  }

  return children;
};
